import { useCallback, useState } from "react";
import SolaCardPaymentModal from "./SolaCardPaymentModal";

/** Thrown when staff close the card window without paying — not a failure to report. */
export class SolaPaymentCancelled extends Error {
    constructor() {
        super("Card payment cancelled");
        this.name = "SolaPaymentCancelled";
    }
}

export const isSolaPaymentCancelled = (error) => error instanceof SolaPaymentCancelled;

/**
 * Lets plain payment-flow functions take a card with a simple `await`:
 *
 *   const { requestSolaCardPayment, solaCardModal } = useSolaCardPayment();
 *   ...
 *   await requestSolaCardPayment({ reservationId, reservationProcess, amount, ... });
 *   ...
 *   return <>{solaCardModal}</>;
 *
 * Resolves with the backend's result once the charge succeeds; rejects with
 * SolaPaymentCancelled if the window is closed first.
 */
const useSolaCardPayment = () => {
    const [request, setRequest] = useState(null);

    const requestSolaCardPayment = useCallback(
        (params) =>
            new Promise((resolve, reject) => {
                setRequest({ params, resolve, reject });
            }),
        [],
    );

    const solaCardModal = request ? (
        <SolaCardPaymentModal
            open
            {...request.params}
            onPaid={(result) => {
                request.resolve(result);
                setRequest(null);
            }}
            onCancel={() => {
                request.reject(new SolaPaymentCancelled());
                setRequest(null);
            }}
        />
    ) : null;

    return { requestSolaCardPayment, solaCardModal };
};

export default useSolaCardPayment;
